// Libraries
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from '@mui/system';

// Core
import { ParameterValues, paramRepository } from '../../core/resources';
import auth from '../../core/authorization';
import FormValidator from '../../core/FormValidator';

// Common
import { gridColumns } from '../../common/dataGrid';
import { TextWithTooltip, ViewTitle } from '../../common/typography';
import { AInputItem, ARadioButtonGroupInputValue, ARadioButtons } from '../../common/inputs';

// Interfaces
import { IFarmDetails } from '../../interfaces/IBusinessEntities';

// Store
import { useAppSelector } from '../../store/hooks';

// API
import documentApi from '../../api/documentApi';
import companyApi from '../../api/companyApi';

// Feature
import { IDocumentAddDialogData, DocumentFileTypeEnum } from '../IDocuments';
import DocumentView from './DocumentView';
import FarmContracts from '../../contracts/FarmContracts';
import Forbidden from '../../core/Forbidden';

export default function FarmDocuments() {
    const { t } = useTranslation(['AnelmaLayout', 'AnelmaFarmFiles', 'AnelmaContract']);

    const context = useAppSelector((state) => state.context);
    const personNames = useAppSelector((state) => state.personNames.data);

    const [loading, setLoading] = useState<boolean>(true);
    const [types, setTypes] = useState<ParameterValues>([]);
    const [farmDetails, setFarmDetails] = useState<IFarmDetails | null>(null);

    const [view, setView] = useState<string>('files');

    useEffect(() => {
        paramRepository.load(['AnelmaFarmFiles']).then(() => {
            setTypes(paramRepository.resource('AnelmaFarmFiles', 'FarmFileSubject'));
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (context?.data?.currentContext) {
            setLoading(true);
            companyApi
                .getCompanyByContext(context.data.currentContext?.context)
                .then((response) => {
                    if (response?.Entity) {
                        let farm = response.Entity as IFarmDetails;
                        setFarmDetails(farm);
                        setLoading(false);
                    } else {
                        setFarmDetails(null);
                        setLoading(false);
                    }
                });
        } else {
            setLoading(false);
            setFarmDetails(null);
        }
    }, [context]);

    const onSave = (data: IDocumentAddDialogData) =>
        documentApi.uploadDocument(data, DocumentFileTypeEnum.FarmDocument);

    const changeView = (view: string) => {
        setView(view);
    };

    const getItems: AInputItem<ARadioButtonGroupInputValue>[] = [
        {
            text: t('AnelmaDocument:1000'),
            value: 'files',
        },
        {
            text: t('AnelmaContract:1000'),
            value: 'contracts',
        },
    ];

    const documentView = (farmDetails: IFarmDetails) => {
        return (
            <DocumentView
                additionalColumns={[
                    gridColumns.dateTime(
                        'ValidFrom',
                        t('AnelmaFarmFiles:1006'),
                        'DD.MM.YYYY HH:mm'
                    ),
                    {
                        field: 'Owner',
                        headerName: t('AnelmaFarmFiles:1007'),
                        renderCell: (params) => (
                            <TextWithTooltip
                                text={String(
                                    personNames.find((_) => _.Owner === params.row.Owner)?.Data ||
                                        '-'
                                )}
                            />
                        ),
                        width: 200,
                    },
                    gridColumns.withTooltip({
                        field: 'Type',
                        headerName: t('AnelmaFarmFiles:1008'),
                        valueGetter: (params, row) =>
                            types.find((type) => `${type.code}` === `${row.Type}`)?.text || '-',
                        width: 200,
                    }),
                ]}
                deleteConfirmation={t('AnelmaFarmFiles:1002')}
                enableAddButton={auth.canCreateFarmDocument}
                enableDeleteFile={auth.canDeleteFarmDocument}
                fileType={DocumentFileTypeEnum.FarmDocument}
                onSave={onSave}
                preLoading={loading}
                types={{
                    label: t('AnelmaFarmFiles:1008'),
                    parameter: 'AnelmaFarmFiles:FarmFileSubject',
                }}
                documentDialogConfig={'farmDocuments'}
                farmId={farmDetails.Id ?? ''}
            />
        );
    };

    const contractsView = (farmDetails: IFarmDetails) => {
        return auth.hasProducerContractAccess || auth.hasAdminContractAccess ? (
            <FarmContracts farm={farmDetails} loading={false} producerView />
        ) : (
            <Forbidden />
        );
    };

    return (
        farmDetails && (
            <Container>
                <ViewTitle>{t('AnelmaLayout:1077')}</ViewTitle>

                <ARadioButtons
                    id='view-toggle'
                    items={getItems}
                    onChange={(e) => changeView(e?.toString() ?? 'files')}
                    validator={new FormValidator()}
                    value={'files'}
                />

                {view === 'files' ? documentView(farmDetails) : contractsView(farmDetails)}
            </Container>
        )
    );
}
